<script lang="ts" setup>
import { SwiperSlide } from 'swiper/vue'
import { useLanguageStore } from '~/store/language'
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import getWidgetTextTranslation from '~/utils/getWidgetTextTranslation'
import parseProductSlug from '~/utils/parseProductSlug'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'
import type { CarouselProduct } from '~/types/Product'

type CategoryProduct = {
  name: string
  product: CarouselProduct
  redirectUrl?: string
}

const props = defineProps<{
  backgroundColor?: string
  category: {
    name: TranslatableString
    productSlug: TranslatableString
    redirectUrl: TranslatableString
  }[]
}>()
const locale = useState<string>('locale.setting')

const categoryProduct = ref<CategoryProduct[]>([])

watch(
  locale,
  async (newLocale) => {
    try {
      const categoryProducts = (await $fetch('/api/hooks/manualCategoryMostPopularProducts', {
        method: 'POST',
        body: {
          locale: newLocale,
          products: props.category.map((category) => parseProductSlug(category.productSlug)),
          langId: useLanguageStore().getLanguageId,
        },
        ...fetchRetryParameters,
        timeout: 3000,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })) as CarouselProduct[] | undefined

      categoryProduct.value = (categoryProducts
        ?.map((product, index) => {
          if (product) {
            return {
              name: getWidgetTextTranslation(props.category[index].name),
              product,
              redirectUrl: getWidgetTextTranslation(props.category[index].redirectUrl),
            }
          }

          return null
        })
        .filter((categoryProduct) => categoryProduct && !categoryProduct.product.is_sold_out) ??
        []) as CategoryProduct[]
    } catch (error) {}
  },
  {
    immediate: true,
  }
)
</script>

<template>
  <section
    v-if="Boolean(categoryProduct.length)"
    :style="{
      backgroundColor: props.backgroundColor,
    }">
    <FullSlider>
      <template #sliderContent>
        <SwiperSlide v-for="({ name, product, redirectUrl }, index) in categoryProduct" :key="index">
          <CategoryProductCard :product="product" :category-name="name" :redirect-url="redirectUrl" />
        </SwiperSlide>
      </template>
    </FullSlider>
  </section>
</template>
